import axiosClient from '@/lib/axios';
import { isAxiosError } from 'axios';
import { queryOptions } from '@tanstack/react-query';

export interface CompanyUpdateInput {
  title: string;
  description: string;
  date: string;
}

export interface CompanyUpdate extends CompanyUpdateInput {
  id: number;
  cid: number;
  created_at: string;
  updated_at: string;
}

export interface CompanyUpdateParams {
  q?: string;
}

export interface CompanyUpdateList {
  data: CompanyUpdate[];
}

export const companyUpdateKeys = {
  company: (companyID: number) => ['companyUpdates', companyID] as const,
  list: (companyID: number, params: CompanyUpdateParams) =>
    [...companyUpdateKeys.company(companyID), 'list', { q: params.q }] as const,
  detail: (companyID: number, companyUpdateID: number) =>
    [...companyUpdateKeys.company(companyID), 'detail', companyUpdateID] as const,
};

const basePath = (companyID: number) => `/companies/${companyID}/company_updates`;

export const isCompanyUpdateDateValid = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

export const companyUpdateListOptions = (companyID: number, params: CompanyUpdateParams) =>
  queryOptions({
    queryKey: companyUpdateKeys.list(companyID, params),
    queryFn: async ({ signal }): Promise<CompanyUpdateList> => {
      const response = await axiosClient.get<CompanyUpdateList>(basePath(companyID), {
        params: { q: params.q },
        signal,
      });
      return response.data;
    },
  });

export const companyUpdateDetailOptions = (companyID: number, companyUpdateID: number) =>
  queryOptions({
    queryKey: companyUpdateKeys.detail(companyID, companyUpdateID),
    queryFn: async ({ signal }): Promise<CompanyUpdate> => {
      const response = await axiosClient.get<{ data: CompanyUpdate }>(`${basePath(companyID)}/${companyUpdateID}`, {
        signal,
      });
      return response.data.data;
    },
  });

export const saveCompanyUpdate = async (companyID: number, values: CompanyUpdateInput, companyUpdateID?: number) => {
  const payload: CompanyUpdateInput = {
    title: values.title.trim(),
    description: values.description.trim(),
    date: values.date,
  };
  const response =
    companyUpdateID === undefined
      ? await axiosClient.post<{ data: CompanyUpdate }>(basePath(companyID), payload)
      : await axiosClient.put<{ data: CompanyUpdate }>(`${basePath(companyID)}/${companyUpdateID}`, payload);
  return response.data.data;
};

export const deleteCompanyUpdate = async (companyID: number, companyUpdateID: number): Promise<void> => {
  await axiosClient.delete(`${basePath(companyID)}/${companyUpdateID}`);
};

export const companyUpdateError = (error: unknown, translate: (key: string) => string): string => {
  if (isAxiosError<{ error?: unknown }>(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 403 || status === 404) return translate(`errors.${status}`);
    if (status === 400) {
      const message = error.response?.data?.error;
      return typeof message === 'string' ? message : translate('errors.400');
    }
  }
  return translate('errors.general');
};
